import { Ingredient, Tag, searchIngredients, listTags } from '../api/index.js';
import { getTagColorHex, tagChipStyle } from '../tags/tagColors.js';
import { getDefaultImageUrl } from '../recipe_renderer/imageUtils.js';

type SearchResultItem =
  | { kind: 'ingredient'; item: Ingredient }
  | { kind: 'tag'; item: Tag };

/**
 * Search section for the home page: one search input filters recipes by
 * *either* ingredient or tag (mixed results in one dropdown, tag rows
 * marked with a "#" instead of an image circle since tags have none) —
 * chosen over two separate search boxes so filtering by category reads
 * as the same kind of thing as filtering by ingredient, not a second
 * unrelated widget to learn. Triggers a callback whenever the combined
 * selection changes.
 */
export class SearchSection {
  private selectedIngredients: Map<string, Ingredient> = new Map();
  private selectedTags: Map<string, Tag> = new Map();
  private onSelectionChanged: (ingredients: Map<string, Ingredient>, tags: Map<string, Tag>) => void;
  private onIngredientAddedToGraph: ((ingredient: Ingredient) => void) | null = null;
  private onIngredientRemovedFromGraph: ((ingredientId: string) => void) | null = null;
  private dropdown: HTMLElement | null = null;
  private chipsContainer: HTMLElement | null = null;
  private searchTimeout: number | null = null;
  private destroyInput: (() => void) | null = null;
  private destroyKeydown: (() => void) | null = null;
  /** Every known tag, for filtering client-side as the user types — a
   *  small, shared dataset, nothing like ingredients' fuzzy-search index. */
  private allTags: Tag[] = [];

  constructor(onSelectionChanged: (ingredients: Map<string, Ingredient>, tags: Map<string, Tag>) => void) {
    this.onSelectionChanged = onSelectionChanged;
  }

  /**
   * Set callbacks for graph-based ingredient filtering.
   */
  setGraphCallbacks(
    onIngredientAdded: (ingredient: Ingredient) => void,
    onIngredientRemoved: (ingredientId: string) => void
  ): void {
    this.onIngredientAddedToGraph = onIngredientAdded;
    this.onIngredientRemovedFromGraph = onIngredientRemoved;
  }

  /**
   * Render the search section into the given container.
   */
  render(container: HTMLElement): void {
    container.innerHTML = '';

    listTags().then((tags) => { this.allTags = tags; }).catch((error) => {
      console.error('Failed to load tags:', error);
    });

    // Search input + dropdown wrapper
    const searchWrapper = document.createElement('div');
    searchWrapper.className = 'relative';

    const input = document.createElement('input');
    input.type = 'text';
    input.id = 'ingredient-search-input';
    input.className =
      'w-full px-4 py-2.5 bg-[#1e1e2e] border border-[#333] rounded-md text-[#e0e0e0] placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent';
    input.placeholder = 'Cerca ingredients o etiquetes...';
    searchWrapper.appendChild(input);

    // Dropdown (custom div-based list, hidden until search results)
    const dropdown = document.createElement('div');
    dropdown.id = 'ingredient-search-dropdown';
    // max-h-[180px] = 5 * the row height (h-9, see showDropdown()) — shows
    // ~5 results, the rest reachable by scrolling rather than cut off.
    dropdown.className =
      'absolute z-20 w-full mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md text-[#e0e0e0] overflow-y-auto max-h-[180px] shadow-lg';
    dropdown.style.display = 'none';
    searchWrapper.appendChild(dropdown);
    this.dropdown = dropdown;

    container.appendChild(searchWrapper);

    // Filter chips container — hidden when empty to avoid vertical gaps
    const chipsContainer = document.createElement('div');
    chipsContainer.id = 'filter-chips';
    chipsContainer.className = 'flex flex-wrap gap-2 mt-3';
    chipsContainer.style.display = 'none';
    container.appendChild(chipsContainer);
    this.chipsContainer = chipsContainer;

    // Wire up events
    const inputHandler = (e: Event) => {
      const query = (e.target as HTMLInputElement).value.trim();
      if (this.searchTimeout !== null) {
        window.clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = window.setTimeout(() => {
        if (query.length > 0) {
          this.performSearch(query);
        } else {
          this.hideDropdown();
        }
      }, 300);
    };
    input.addEventListener('input', inputHandler);
    this.destroyInput = () => input.removeEventListener('input', inputHandler);

    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hideDropdown();
      }
    };
    input.addEventListener('keydown', keydownHandler);
    this.destroyKeydown = () => input.removeEventListener('keydown', keydownHandler);

    // Click outside to close dropdown
    const outsideClickHandler = (e: MouseEvent) => {
      if (!searchWrapper.contains(e.target as Node)) {
        this.hideDropdown();
      }
    };
    document.addEventListener('click', outsideClickHandler);

    // Store for cleanup
    (this as any).destroyOutsideClick = () =>
      document.removeEventListener('click', outsideClickHandler);
  }

  private lastResults: SearchResultItem[] = [];

  private async performSearch(query: string): Promise<void> {
    try {
      // Fetch more than the ~5 that fit in the dropdown's fixed visible
      // height (see showDropdown()) — some queries genuinely match many
      // ingredients, reachable by scrolling instead of just cut off.
      const ingredients = await searchIngredients(query, 20);
      const q = query.toLowerCase();
      const matchingTags = this.allTags.filter(t => t.name.toLowerCase().includes(q));
      this.lastResults = [
        ...ingredients.map((item): SearchResultItem => ({ kind: 'ingredient', item })),
        ...matchingTags.map((item): SearchResultItem => ({ kind: 'tag', item })),
      ];
      this.showDropdown();
    } catch (error) {
      console.error('Failed to search ingredients:', error);
      this.lastResults = [];
      this.showDropdown();
    }
  }

  private showDropdown(): void {
    if (!this.dropdown) return;

    this.dropdown.innerHTML = '';

    const available = this.lastResults.filter((r) =>
      r.kind === 'ingredient' ? !this.selectedIngredients.has(r.item.id) : !this.selectedTags.has(r.item.id)
    );

    if (available.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'px-4 py-2 text-[#888] text-sm';
      noResults.textContent = 'No s\'han trobat resultats';
      this.dropdown.appendChild(noResults);
    } else {
      for (const result of available) {
        const row = document.createElement('div');
        row.className =
          'h-9 px-4 py-2 text-sm hover:bg-[var(--accent-alpha-13)] cursor-pointer transition-colors flex items-center gap-2';

        if (result.kind === 'ingredient') {
          const ingImageUrl = getDefaultImageUrl(result.item.images);
          if (ingImageUrl) {
            const img = document.createElement('img');
            img.src = ingImageUrl;
            img.alt = result.item.name;
            img.className = 'rounded-full';
            img.style.cssText = 'width: 20px; height: 20px; object-fit: cover; flex-shrink: 0;';
            row.appendChild(img);
          }
        } else {
          // Tags have no image — a plain "#" marks the row as a tag
          // instead, in the image circle's place.
          const hash = document.createElement('span');
          hash.className = 'font-mono text-sm flex-shrink-0';
          hash.style.cssText = `width: 20px; text-align: center; color: ${getTagColorHex(result.item.color)};`;
          hash.textContent = '#';
          row.appendChild(hash);
        }

        const nameSpan = document.createElement('span');
        nameSpan.textContent = result.item.name;
        row.appendChild(nameSpan);

        row.addEventListener('click', () => {
          if (result.kind === 'ingredient') {
            this.addIngredient(result.item);
          } else {
            this.addTag(result.item);
          }
          this.hideDropdown();
          // Clear input
          const input = document.getElementById('ingredient-search-input') as HTMLInputElement;
          if (input) input.value = '';
        });
        this.dropdown.appendChild(row);
      }
    }

    this.dropdown.style.display = 'block';
  }

  private hideDropdown(): void {
    if (this.dropdown) {
      this.dropdown.style.display = 'none';
    }
  }

  private notifyChanged(): void {
    this.onSelectionChanged(new Map(this.selectedIngredients), new Map(this.selectedTags));
  }

  private addIngredient(ingredient: Ingredient): void {
    if (this.selectedIngredients.has(ingredient.id)) return;

    this.selectedIngredients.set(ingredient.id, ingredient);
    this.renderChips();
    this.notifyChanged();

    // Also trigger graph callback if set
    if (this.onIngredientAddedToGraph) {
      this.onIngredientAddedToGraph(ingredient);
    }
  }

  removeIngredient(ingredientId: string): void {
    this.selectedIngredients.delete(ingredientId);
    this.renderChips();
    this.notifyChanged();

    // Also trigger graph callback if set
    if (this.onIngredientRemovedFromGraph) {
      this.onIngredientRemovedFromGraph(ingredientId);
    }
  }

  private addTag(tag: Tag): void {
    if (this.selectedTags.has(tag.id)) return;
    this.selectedTags.set(tag.id, tag);
    this.renderChips();
    this.notifyChanged();
  }

  removeTag(tagId: string): void {
    this.selectedTags.delete(tagId);
    this.renderChips();
    this.notifyChanged();
  }

  private renderChips(): void {
    if (!this.chipsContainer) return;

    this.chipsContainer.innerHTML = '';

    if (this.selectedIngredients.size === 0 && this.selectedTags.size === 0) {
      this.chipsContainer.style.display = 'none';
      return;
    }
    this.chipsContainer.style.display = 'flex';

    for (const [id, ingredient] of this.selectedIngredients) {
      const chip = document.createElement('span');
      chip.className =
        'inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--accent-alpha-13)] border border-[var(--accent-alpha-27)] rounded-full text-[var(--accent)] text-sm font-mono';

      // Image circle (if available)
      const chipImageUrl = getDefaultImageUrl(ingredient.images);
      if (chipImageUrl) {
        const img = document.createElement('img');
        img.src = chipImageUrl;
        img.alt = ingredient.name;
        img.className = 'rounded-full';
        img.style.cssText = 'width: 18px; height: 18px; object-fit: cover; flex-shrink: 0;';
        chip.appendChild(img);
      }

      const name = document.createElement('span');
      name.textContent = ingredient.name;
      chip.appendChild(name);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className =
        'ml-1 text-[var(--accent)] hover:text-[#ef4444] font-bold leading-none text-xs';
      removeBtn.textContent = '×';
      removeBtn.title = `Elimina "${ingredient.name}"`;
      removeBtn.addEventListener('click', () => this.removeIngredient(id));
      chip.appendChild(removeBtn);

      this.chipsContainer.appendChild(chip);
    }

    for (const [id, tag] of this.selectedTags) {
      const chip = document.createElement('span');
      chip.className = 'inline-flex items-center gap-1.5 px-3 py-1 border rounded-full text-sm font-mono';
      chip.style.cssText = tagChipStyle(tag.color);

      const hash = document.createElement('span');
      hash.textContent = '#';
      chip.appendChild(hash);

      const name = document.createElement('span');
      name.textContent = tag.name;
      chip.appendChild(name);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'ml-1 hover:text-[#ef4444] font-bold leading-none text-xs';
      removeBtn.style.color = getTagColorHex(tag.color);
      removeBtn.textContent = '×';
      removeBtn.title = `Elimina "${tag.name}"`;
      removeBtn.addEventListener('click', () => this.removeTag(id));
      chip.appendChild(removeBtn);

      this.chipsContainer.appendChild(chip);
    }
  }

  /**
   * Disable the search input (used during edit mode).
   */
  disable(): void {
    const input = document.getElementById('ingredient-search-input') as HTMLInputElement;
    if (input) {
      input.disabled = true;
      input.placeholder = 'Cerca desactivada durant edició';
      input.style.opacity = '0.5';
      input.style.cursor = 'not-allowed';
    }
  }

  /**
   * Enable the search input (after edit mode).
   */
  enable(): void {
    const input = document.getElementById('ingredient-search-input') as HTMLInputElement;
    if (input) {
      input.disabled = false;
      input.placeholder = 'Cerca ingredients o etiquetes...';
      input.style.opacity = '1';
      input.style.cursor = 'text';
    }
  }

  /**
   * Clean up all event listeners.
   */
  destroy(): void {
    if (this.destroyInput) this.destroyInput();
    if (this.destroyKeydown) this.destroyKeydown();
    if ((this as any).destroyOutsideClick) (this as any).destroyOutsideClick();
    if (this.searchTimeout !== null) {
      window.clearTimeout(this.searchTimeout);
    }
  }
}
